import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { IconLogout } from '../components/Icons';
import SiteMapCanvas from '../components/SiteMapCanvas';
import BleProvisionFlow from './technician/BleProvisionFlow';
import { isNativeApp, secureDevice } from '../native/ble';

/**
 * Phase 4 PR 3.B — Technician App shell.
 *
 * A fundamentally different surface from CustomerHome / AdminDashboard:
 * - mobile-first single column (technicians work on a phone in the
 *   field, not on a desk)
 * - no sidebar — every screen has a top-bar back button instead
 * - the only data the technician sees is the sites assigned to them
 *
 * PR 3.B delivers two screens:
 *   1. Sites list (cards with progress)
 *   2. Site detail with a Map / List toggle
 *
 * PR 3.C will replace the placeholder gate sheet with the real
 * install wizard.
 */

const STATUS_META = {
  not_started: { color: '#dc2626', label: 'טרם הותקן' },
  in_progress: { color: '#f59e0b', label: 'בתהליך' },
  completed:   { color: '#16a34a', label: 'הותקן' },
  failed:      { color: '#7f1d1d', label: 'נכשל' },
};

const STATUS_ORDER = ['not_started', 'in_progress', 'failed', 'completed'];

function statusMeta(status) {
  return STATUS_META[status] || STATUS_META.not_started;
}

function TechnicianApp({ user, setUser }) {
  const handleLogout = async () => {
    await axios.post('/api/auth/logout');
    setUser(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f7f8fa' }}>
      <TechnicianTopBar userName={user.name} onLogout={handleLogout} />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '0.85rem' }}>
        <Routes>
          <Route path="/" element={<SitesListScreen userName={user.name} />} />
          <Route path="/sites/:id" element={<SiteDetailScreen />} />
        </Routes>
      </div>
    </div>
  );
}

function TechnicianTopBar({ userName, onLogout }) {
  return (
    <div style={{
      background: '#1a1a2e',
      color: '#fff',
      padding: '0.7rem 0.95rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.65rem',
      position: 'sticky',
      top: 0,
      zIndex: 40,
      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
    }}>
      <img
        src="/logo-icon.png"
        alt="Huri-Connect"
        style={{
          width: 36,
          height: 'auto',
          background: '#fff',
          padding: 4,
          borderRadius: 6,
          flexShrink: 0,
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.1 }}>
          Huri-Connect
        </span>
        <span style={{
          fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          טכנאי · {userName}
        </span>
      </div>
      <button
        type="button"
        onClick={onLogout}
        title="יציאה"
        style={{
          background: 'transparent',
          border: '1px solid rgba(255,255,255,0.2)',
          color: 'rgba(255,255,255,0.85)',
          padding: '0.45rem 0.65rem',
          borderRadius: 6,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.35rem',
          fontFamily: 'Heebo, sans-serif',
          fontSize: '0.8rem',
          minHeight: 44,
        }}
      >
        <IconLogout size={16} />
        יציאה
      </button>
    </div>
  );
}

function SitesListScreen({ userName }) {
  const [sites, setSites] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/technician/sites')
      .then(res => setSites(res.data))
      .catch(err => setError(err.response?.data?.error || 'שגיאה בטעינה'));
  }, []);

  if (error) {
    return <div className="loading" style={{ height: 'auto', padding: '2rem' }}>{error}</div>;
  }
  if (sites === null) return <div className="loading">טוען...</div>;

  const openSitesCount = sites.filter(s => s.gates_completed < s.gates_total).length;

  return (
    <>
      <div style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '0.95rem 1.05rem',
        marginBottom: '0.85rem',
      }}>
        <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>שלום {userName}</div>
        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>
          {sites.length === 0
            ? 'אין כרגע אתרים משויכים אליך.'
            : `${openSitesCount} אתרים פתוחים מתוך ${sites.length}`}
        </div>
      </div>

      {sites.length === 0 && (
        <div style={{
          background: '#fff',
          border: '1px dashed #e5e7eb',
          borderRadius: 8,
          padding: '2rem 1rem',
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: '0.9rem',
        }}>
          ברגע שמנהל ישייך אליך אתר, הוא יופיע כאן.
        </div>
      )}

      {sites.map(s => (
        <SiteCard key={s.id} site={s} onClick={() => navigate(`/sites/${s.id}`)} />
      ))}
    </>
  );
}

function SiteCard({ site, onClick }) {
  const total = site.gates_total || 0;
  const done = site.gates_completed || 0;
  const ratio = total === 0 ? 0 : done / total;
  const pct = Math.round(ratio * 100);

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'right',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '0.95rem 1.05rem',
        marginBottom: '0.7rem',
        cursor: 'pointer',
        fontFamily: 'Heebo, sans-serif',
        minHeight: 84,
      }}
    >
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.2rem' }}>
        {site.customer_name || '—'}
      </div>
      <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1a1a2e' }}>
        {site.name}
      </div>
      {site.address && (
        <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.15rem' }}>
          {site.address}
        </div>
      )}

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.55rem',
        marginTop: '0.7rem',
      }}>
        <div style={{
          flex: 1,
          height: 8,
          background: '#f3f4f6',
          borderRadius: 999,
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${pct}%`,
            height: '100%',
            background: ratio >= 1 ? '#16a34a' : '#3b82f6',
            transition: 'width 0.2s',
          }} />
        </div>
        <span style={{ fontSize: '0.78rem', color: '#374151', whiteSpace: 'nowrap' }}>
          {done} / {total} שערים הותקנו
        </span>
      </div>

      {(site.gates_in_progress > 0 || site.gates_failed > 0) && (
        <div style={{
          fontSize: '0.74rem',
          color: '#6b7280',
          marginTop: '0.4rem',
          display: 'flex',
          gap: '0.6rem',
        }}>
          {site.gates_in_progress > 0 && <span>{site.gates_in_progress} בתהליך</span>}
          {site.gates_failed > 0 && (
            <span style={{ color: '#b91c1c' }}>{site.gates_failed} כשל</span>
          )}
        </div>
      )}
    </button>
  );
}

function SiteDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState('map'); // 'map' | 'list' (industrial only)
  const [selectedGate, setSelectedGate] = useState(null);
  // Phase 5 — when a gate's product requires paired install, the
  // technician picks a role here BEFORE the wizard opens, so
  // start-install knows whether to file the new device to the
  // control or lock FK on the gate.
  const [pendingRoleGate, setPendingRoleGate] = useState(null);

  const load = useCallback(() => {
    axios.get(`/api/technician/sites/${id}`)
      .then(res => setSite(res.data))
      .catch(err => setError(err.response?.data?.error || 'שגיאה בטעינה'));
  }, [id]);

  useEffect(load, [load]);

  if (error) {
    return (
      <div style={{ padding: '1rem' }}>
        <BackBar onBack={() => navigate('/')} />
        <div style={{ marginTop: '1rem', color: '#b91c1c' }}>{error}</div>
      </div>
    );
  }
  if (site === null) return <div className="loading">טוען...</div>;

  const statusCounts = site.gates.reduce((acc, g) => {
    acc[g.installation_status] = (acc[g.installation_status] || 0) + 1;
    return acc;
  }, {});
  const completed = statusCounts.completed || 0;
  const inProgress = statusCounts.in_progress || 0;
  const failed = statusCounts.failed || 0;
  const total = site.gates.length;

  return (
    <>
      <BackBar onBack={() => navigate('/')} />

      <div style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '0.85rem 1rem',
        marginBottom: '0.7rem',
      }}>
        <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
          {site.customer_name || '—'}
        </div>
        <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>{site.name}</div>
        {site.address && (
          <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.15rem' }}>
            {site.address}
          </div>
        )}
        <div style={{ fontSize: '0.82rem', color: '#374151', marginTop: '0.55rem' }}>
          {completed} / {total} הותקנו
          {inProgress > 0 && <> · <span style={{ color: '#b45309' }}>{inProgress} בתהליך</span></>}
          {failed > 0 && <> · <span style={{ color: '#b91c1c' }}>{failed} כשל</span></>}
        </div>
      </div>

      {/* Phase 5 — residential sites don't have a floor-plan map to
          drag gates onto, so the map/list toggle is hidden and the
          list view is the only surface. Industrial sites keep both. */}
      {site.type === 'residential' ? (
        <ListView gates={site.gates} onGateClick={(g) => onPickGate(g, setSelectedGate, setPendingRoleGate)} />
      ) : (
        <>
          <ViewToggle view={view} onChange={setView} />

          {view === 'map' ? (
            <MapView site={site} onGateClick={(g) => onPickGate(g, setSelectedGate, setPendingRoleGate)} />
          ) : (
            <ListView gates={site.gates} onGateClick={(g) => onPickGate(g, setSelectedGate, setPendingRoleGate)} />
          )}
        </>
      )}

      {pendingRoleGate && (
        <RoleChooserModal
          gate={pendingRoleGate}
          onPick={(role) => {
            setSelectedGate({ ...pendingRoleGate, _installRole: role });
            setPendingRoleGate(null);
          }}
          onClose={() => setPendingRoleGate(null)}
        />
      )}

      {selectedGate && (
        <InstallWizard
          gate={selectedGate}
          role={selectedGate._installRole || null}
          siteId={parseInt(id, 10)}
          onClose={(didChange) => {
            setSelectedGate(null);
            if (didChange) load();
          }}
        />
      )}
    </>
  );
}

// Phase 5 — gate tap dispatcher. Industrial gates go straight to the
// wizard; paired-install residential gates open a role chooser first
// so the wizard knows whether the new device is going to be the
// control side (2PM driving open/close) or the lock side (relay on
// the RSK safety circuit).
function onPickGate(gate, setSelectedGate, setPendingRoleGate) {
  const requiresPaired = !!gate.product?.requires_paired_install;
  if (requiresPaired) {
    setPendingRoleGate(gate);
  } else {
    setSelectedGate(gate);
  }
}

function RoleChooserModal({ gate, onPick, onClose }) {
  const controlReady = !!gate.control_device_id;
  const lockReady = !!gate.lock_device_id;
  const bothReady = controlReady && lockReady;

  // Phase 5 — ENFORCED ORDER: lock device must be installed FIRST.
  // When the technician arrives, the gate's RSK circuit is open
  // (= gate locked, motor inert). Installing the 2PM and trying to
  // test it before the lock relay closes the circuit means a 2PM
  // pulse goes nowhere. So we keep the control button disabled
  // until the lock device is in.
  const controlDisabled = !lockReady;

  const Btn = ({ role, title, sub, ready, disabled }) => (
    <button
      type="button"
      onClick={disabled ? undefined : () => onPick(role)}
      disabled={disabled}
      style={{
        width: '100%',
        textAlign: 'start',
        padding: '0.9rem 1rem',
        marginBottom: '0.6rem',
        background: disabled ? '#F1F5F9' : '#ffffff',
        border: '2px solid ' + (
          disabled ? '#CBD5E1' : ready ? '#22C55E' : '#1B3A5C'
        ),
        borderRadius: '12px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.7 : 1,
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
      }}
    >
      <span style={{
        width: 36, height: 36, borderRadius: '50%',
        background: disabled ? '#E2E8F0' : ready ? '#DCFCE7' : '#E0E7FF',
        color: disabled ? '#94A3B8' : ready ? '#15803D' : '#1B3A5C',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: '1.1rem', flexShrink: 0,
      }}>{ready ? '✓' : (role === 'control' ? '⚙' : '🔒')}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 700, fontSize: '1rem',
          color: disabled ? '#64748b' : '#1B3A5C',
        }}>
          {title}{ready && <span style={{ color: '#15803D', fontWeight: 500, fontSize: '0.78rem', marginInlineStart: '0.5rem' }}>(מותקן — להחלפה)</span>}
        </div>
        <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '0.15rem' }}>{sub}</div>
      </span>
    </button>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(27,58,92,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FAF7F2', borderTopLeftRadius: '20px', borderTopRightRadius: '20px',
          padding: '1.5rem 1.25rem', width: '100%', maxWidth: 500, direction: 'rtl',
        }}
      >
        <h2 style={{ fontSize: '1.15rem', marginBottom: '0.4rem', color: '#1B3A5C' }}>
          איזה רכיב מתקין?
        </h2>
        <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.55, marginBottom: '1rem' }}>
          {bothReady
            ? 'שני הרכיבים כבר מותקנים. בחירה בכל אחד תפעיל מחדש את ההתקנה שלו (force=true).'
            : !lockReady
              ? 'מתחילים מרכיב הנעילה — הוא חייב לסגור את מעגל ה-RSK לפני שאפשר להפעיל את רכיב הפתיחה.'
              : 'רכיב הנעילה מותקן והשער יכול לזוז. עכשיו התקן את רכיב הפתיחה.'}
        </p>
        <Btn
          role="lock"
          title="רכיב נעילה (Plus 1 על RSK)"
          sub="התקן ראשון. סוגר אוטומטית את מעגל הנעילה כדי שהשער יוכל לזוז."
          ready={lockReady}
          disabled={false}
        />
        <Btn
          role="control"
          title="רכיב פתיחה (2PM)"
          sub={controlDisabled
            ? 'יזמין אחרי התקנת רכיב הנעילה.'
            : 'מחובר לקלט STEP של בקר השער. שולח פולס לפתוח/לסגור.'}
          ready={controlReady}
          disabled={controlDisabled}
        />
        <button
          type="button"
          onClick={onClose}
          style={{
            width: '100%', padding: '0.75rem', marginTop: '0.4rem',
            background: 'transparent', border: 'none',
            color: '#64748b', fontFamily: 'inherit', fontSize: '0.9rem',
            cursor: 'pointer',
          }}
        >ביטול</button>
      </div>
    </div>
  );
}

function BackBar({ onBack }) {
  return (
    <button
      type="button"
      onClick={onBack}
      style={{
        background: 'transparent',
        border: 'none',
        color: '#374151',
        fontSize: '0.9rem',
        padding: '0.4rem 0',
        cursor: 'pointer',
        marginBottom: '0.3rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.35rem',
        fontFamily: 'Heebo, sans-serif',
        minHeight: 40,
      }}
    >
      <span style={{ fontSize: '1.05rem' }}>›</span>
      חזרה לכל האתרים
    </button>
  );
}

function ViewToggle({ view, onChange }) {
  const btn = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => onChange(key)}
      style={{
        flex: 1,
        padding: '0.6rem',
        border: 'none',
        background: view === key ? '#1a1a2e' : 'transparent',
        color: view === key ? '#fff' : '#374151',
        fontSize: '0.9rem',
        fontWeight: 500,
        cursor: 'pointer',
        fontFamily: 'Heebo, sans-serif',
        minHeight: 44,
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{
      display: 'flex',
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      overflow: 'hidden',
      marginBottom: '0.7rem',
    }}>
      {btn('map', 'מפה')}
      <div style={{ width: 1, background: '#e5e7eb' }} />
      {btn('list', 'רשימה')}
    </div>
  );
}

function MapView({ site, onGateClick }) {
  const statusRingColorFor = useCallback(
    (gate) => statusMeta(gate.installation_status).color,
    [],
  );
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: '0.5rem',
    }}>
      <SiteMapCanvas
        gates={site.gates}
        layout={site.layout}
        editable={false}
        onGateClick={onGateClick}
        statusRingColorFor={statusRingColorFor}
      />
    </div>
  );
}

function ListView({ gates, onGateClick }) {
  const sorted = useMemo(() => {
    const copy = [...gates];
    copy.sort((a, b) => {
      const ai = STATUS_ORDER.indexOf(a.installation_status);
      const bi = STATUS_ORDER.indexOf(b.installation_status);
      if (ai !== bi) return ai - bi;
      return a.name.localeCompare(b.name, 'he');
    });
    return copy;
  }, [gates]);

  if (sorted.length === 0) {
    return (
      <div style={{
        background: '#fff',
        border: '1px dashed #e5e7eb',
        borderRadius: 8,
        padding: '2rem 1rem',
        textAlign: 'center',
        color: '#9ca3af',
      }}>
        אין שערים באתר זה.
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      overflow: 'hidden',
    }}>
      {sorted.map((g, i) => {
        const meta = statusMeta(g.installation_status);
        return (
          <button
            key={g.id}
            type="button"
            onClick={() => onGateClick(g)}
            style={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              gap: '0.7rem',
              padding: '0.85rem 0.95rem',
              border: 'none',
              background: '#fff',
              borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
              cursor: 'pointer',
              textAlign: 'right',
              fontFamily: 'Heebo, sans-serif',
              minHeight: 60,
            }}
          >
            <div style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: meta.color,
              flexShrink: 0,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 500, color: '#1a1a2e' }}>
                {g.name}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                {g.model || '—'}
              </div>
            </div>
            <span style={{
              fontSize: '0.74rem',
              padding: '0.2rem 0.55rem',
              borderRadius: 999,
              background: `${meta.color}15`,
              color: meta.color,
              fontWeight: 600,
              flexShrink: 0,
            }}>
              {meta.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Install wizard (PR 3.C)
// ---------------------------------------------------------------------------

// Shared button style — every tappable surface in the wizard is at least
// 48 px tall per the mobile-first constraint.
const BTN_PRIMARY = {
  display: 'block',
  width: '100%',
  background: '#1a1a2e',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '0.85rem 1rem',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'Heebo, sans-serif',
  minHeight: 48,
};

const BTN_SECONDARY = {
  ...BTN_PRIMARY,
  background: '#fff',
  color: '#1a1a2e',
  border: '1px solid #d1d5db',
};

const BTN_DISABLED = {
  ...BTN_PRIMARY,
  background: '#e5e7eb',
  color: '#9ca3af',
  cursor: 'not-allowed',
};

function ModalShell({ title, onClose, children }) {
  // Common bottom-sheet chrome shared by every wizard step. We still
  // overlay the backdrop, but clicking it is *not* a close shortcut —
  // mid-install accidental dismissals are how technicians lose passwords,
  // so the user must hit the explicit × button.
  return (
    <>
      <div style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 80,
      }} />
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTopLeftRadius: 14,
        borderTopRightRadius: 14,
        padding: '1rem 1.1rem 1.5rem',
        zIndex: 90,
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
      }}>
        <div style={{
          width: 36, height: 4, background: '#e5e7eb',
          borderRadius: 999, margin: '0 auto 0.85rem',
        }} />
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', marginBottom: '0.85rem',
        }}>
          <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>{title}</div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none',
              color: '#6b7280', fontSize: '1.3rem', cursor: 'pointer',
              padding: '0.25rem 0.5rem', minHeight: 40, minWidth: 40,
            }}
            aria-label="סגור"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </>
  );
}

function FailLink({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 'none',
        color: '#b91c1c',
        fontSize: '0.85rem',
        cursor: 'pointer',
        textDecoration: 'underline',
        padding: '0.5rem 0',
        marginTop: '1rem',
        fontFamily: 'Heebo, sans-serif',
        minHeight: 40,
        width: '100%',
      }}
    >
      סמן ככשלון
    </button>
  );
}

function FailForm({ onCancel, onSubmit, submitting }) {
  const [notes, setNotes] = useState('');
  return (
    <div>
      <div style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.6rem' }}>
        סימון ההתקנה ככשלון
      </div>
      <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.6rem' }}>
        תאר בקצרה מה לא הצליח. ההערה תוצג למנהל ולצוות בחורי.
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={4}
        style={{
          width: '100%',
          padding: '0.65rem 0.75rem',
          border: '1px solid #d1d5db',
          borderRadius: 6,
          fontSize: '0.95rem',
          fontFamily: 'Heebo, sans-serif',
          resize: 'vertical',
          boxSizing: 'border-box',
        }}
        placeholder="לדוגמה: ה-WiFi של הלקוח לא יציב, נדרש router אחר."
      />
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.85rem' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          style={BTN_SECONDARY}
        >
          ביטול
        </button>
        <button
          type="button"
          onClick={() => onSubmit(notes)}
          disabled={submitting}
          style={submitting ? BTN_DISABLED : { ...BTN_PRIMARY, background: '#b91c1c' }}
        >
          {submitting ? 'שומר...' : 'שמור וסיים'}
        </button>
      </div>
    </div>
  );
}

function CopyRow({ label, value, isPassword }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard API can fail on insecure origins / older browsers.
      // Fail silently — the technician can still read & type the value.
    }
  };
  return (
    <div style={{ marginBottom: '0.7rem' }}>
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.2rem' }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'stretch' }}>
        <div style={{
          flex: 1,
          background: isPassword ? '#fffbeb' : '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          padding: '0.6rem 0.7rem',
          fontFamily: isPassword ? 'monospace' : 'inherit',
          fontSize: '0.95rem',
          wordBreak: 'break-all',
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
        }}>
          {value}
        </div>
        <button
          type="button"
          onClick={onCopy}
          style={{
            background: copied ? '#16a34a' : '#1a1a2e',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '0 0.85rem',
            fontSize: '0.85rem',
            cursor: 'pointer',
            minHeight: 48,
            minWidth: 80,
            fontFamily: 'Heebo, sans-serif',
            whiteSpace: 'nowrap',
          }}
          aria-label={`העתק ${label}`}
        >
          {copied ? '✓ הועתק' : 'העתק'}
        </button>
      </div>
    </div>
  );
}

function InstallWizard({ gate, siteId, onClose, role }) {
  // ``didChange`` flag bubbles back to parent so the gates list reloads
  // (and the map flips colour) only when the technician actually moved
  // the install state. Plain backdrop dismissals don't trigger a reload.
  const [didChange, setDidChange] = useState(false);
  const close = () => onClose(didChange);

  // Resume logic: if the technician taps a gate that's already
  // ``in_progress`` we can't recover the original password (it's
  // in-memory only, by design — see CLAUDE.md), so we dump them back
  // at preflight and let them rotate by re-running start-install with
  // ``force=true``.
  const initialStep =
    gate.installation_status === 'completed' ? 'completed_resume'
    : gate.installation_status === 'failed' ? 'failed_resume'
    : 'preflight';

  const [step, setStep] = useState(initialStep);
  const [creds, setCreds] = useState(null);  // { mqtt_username, broker_host, broker_username, broker_password, device_auth_password, ... }
  // Phase 4.5 — BLE handle captured when the automatic flow provisions a
  // device ({ deviceId, realm }). Lets the wizard lock the Shelly UI with
  // its password AFTER the open/close/lock tests pass, so the device
  // stays open for troubleshooting throughout. Null for the manual flow.
  const [bleCtx, setBleCtx] = useState(null);
  // Phase 4.7 — wiring layout. Was a per-install choice; since June 2026
  // we install only ``toggle_with_stop`` (the layout the hermetic-lock
  // story depends on), so the wizard no longer asks. Existing gates with
  // ``open_close_separate`` keep working but no new ones get created.
  const wiringType = 'toggle_with_stop';
  // Opening height (metres) — feeds the auto-compute path for the
  // gate's movement timeout. Optional; if left blank we keep the
  // server's fallback. String state so the <input type=number>
  // can show partial input like "2." while the operator is typing.
  const [openingHeight, setOpeningHeight] = useState(
    gate.opening_height_meters != null ? String(gate.opening_height_meters) : ''
  );
  // Product override — the office picked something when creating
  // the gate but the technician on-site might know better. NULL =
  // "no change from current". start-install diffs against
  // gate.product_id and stamps the swap into installation_notes
  // when the value actually changes.
  const [productId, setProductId] = useState(
    gate.product_id ? String(gate.product_id) : ''
  );
  // Gate-detail booleans captured on the new "פרטי השער" step.
  // Default False for both per the product call — the majority of
  // installs don't have these features, and the step should feel
  // fast to skip-through when nothing applies.
  const [hasBuiltInAutoClose, setHasBuiltInAutoClose] = useState(
    !!gate.has_built_in_auto_close
  );
  const [isContinuousWithLeveler, setIsContinuousWithLeveler] = useState(
    !!gate.is_continuous_with_leveler
  );
  const [showFail, setShowFail] = useState(false);
  const [failSubmitting, setFailSubmitting] = useState(false);

  const submitFail = async (notes) => {
    setFailSubmitting(true);
    try {
      await axios.post(
        `/api/technician/gates/${gate.id}/fail-install`,
        { notes },
      );
      setDidChange(true);
      onClose(true);
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בשמירת הכשלון');
      setFailSubmitting(false);
    }
  };

  const titleByStep = {
    preflight: 'בדיקות לפני התקנה',
    details: 'פרטי השער',
    network: 'חיבור ה-Shelly לרשת',
    identify: 'הזנת ה-MAC',
    script: 'הרצת סקריפט ההגדרה',
    waiting: 'ממתינים ל-Shelly',
    testing: 'בדיקת השער',
    success: 'הסתיים בהצלחה',
    completed_resume: 'השער כבר הותקן',
    failed_resume: 'ההתקנה סומנה ככשלון',
  };

  return (
    <ModalShell title={`${gate.name} · ${titleByStep[step] || ''}`} onClose={close}>
      {showFail ? (
        <FailForm
          onCancel={() => setShowFail(false)}
          onSubmit={submitFail}
          submitting={failSubmitting}
        />
      ) : (
        <>
          {step === 'preflight' && (
            <PreflightStep
              role={role}
              onContinue={() => setStep(role === 'lock' ? 'network' : 'details')}
            />
          )}
          {step === 'details' && (
            <DetailsStep
              gate={gate}
              role={role}
              productId={productId}
              onProductIdChange={setProductId}
              openingHeight={openingHeight}
              onOpeningHeightChange={setOpeningHeight}
              hasBuiltInAutoClose={hasBuiltInAutoClose}
              onHasBuiltInAutoCloseChange={setHasBuiltInAutoClose}
              isContinuousWithLeveler={isContinuousWithLeveler}
              onIsContinuousWithLevelerChange={setIsContinuousWithLeveler}
              onContinue={() => setStep('network')}
            />
          )}
          {step === 'network' && (
            <NetworkStep
              gate={gate}
              siteId={siteId}
              wiringType={wiringType}
              openingHeight={openingHeight}
              productId={productId}
              hasBuiltInAutoClose={hasBuiltInAutoClose}
              isContinuousWithLeveler={isContinuousWithLeveler}
              role={role}
              onManual={() => setStep('identify')}
              onProvisioned={(c, ctx) => {
                setCreds(c);
                setBleCtx(ctx || null);
                setDidChange(true);
                setStep('waiting');
              }}
            />
          )}
          {step === 'identify' && (
            <IdentifyStep
              gate={gate}
              wiringType={wiringType}
              openingHeight={openingHeight}
              productId={productId}
              hasBuiltInAutoClose={hasBuiltInAutoClose}
              isContinuousWithLeveler={isContinuousWithLeveler}
              role={role}
              onProvisioned={(c) => {
                setCreds(c);
                setDidChange(true);
                setStep('script');
              }}
            />
          )}
          {step === 'script' && creds && (
            <ScriptStep
              gate={gate}
              creds={creds}
              wiringType={wiringType}
              role={role}
              onContinue={() => setStep('waiting')}
            />
          )}
          {step === 'waiting' && (
            <WaitingStep
              gate={gate}
              role={role}
              onAlive={() => setStep('testing')}
            />
          )}
          {step === 'testing' && (
            <TestingStep
              gate={gate}
              wiringType={wiringType}
              role={role}
              onContinue={() => {
                // Lock the device UI now that the gate is proven working
                // (BLE flow only). Manual installs skip straight to done.
                if (bleCtx && bleCtx.realm && creds && creds.device_auth_password) {
                  setStep('securing');
                } else {
                  setStep('success');
                }
              }}
            />
          )}
          {step === 'securing' && (
            <SecuringStep
              bleCtx={bleCtx}
              password={creds && creds.device_auth_password}
              onDone={() => setStep('success')}
            />
          )}
          {step === 'success' && (
            <SuccessStep
              gate={gate}
              role={role}
              onClose={() => {
                setDidChange(true);
                onClose(true);
              }}
            />
          )}
          {step === 'completed_resume' && (
            <CompletedResumeView
              gate={gate}
              onReinstall={() => setStep('preflight')}
              onClose={close}
            />
          )}
          {step === 'failed_resume' && (
            <FailedResumeView
              gate={gate}
              onReinstall={() => setStep('preflight')}
              onClose={close}
            />
          )}

          {/* Fail-out is allowed at every step except the terminal "success" */}
          {step !== 'success'
            && step !== 'securing'
            && step !== 'completed_resume'
            && step !== 'failed_resume'
            && (
              <FailLink onClick={() => setShowFail(true)} />
          )}
        </>
      )}
    </ModalShell>
  );
}

function PreflightStep({ onContinue, role }) {
  const [confirmed, setConfirmed] = useState(false);
  const isLock = role === 'lock';
  return (
    <div>
      <div style={{ fontSize: '0.88rem', color: '#374151', marginBottom: '0.85rem' }}>
        {isLock ? (
          <>
            רכיב הנעילה (Shelly Plus 1) מתחבר למעגל ה-RSK של בקר השער דרך
            מגעי NO. אחרי ההתקנה — המעגל ייסגר אוטומטית והשער יוכל לזוז,
            כדי שתוכל להמשיך להתקנת רכיב הפתיחה.
          </>
        ) : (
          'לפני שנחבר את ה-Shelly לרשת, וודא שכל החיבורים הפיזיים הושלמו.'
        )}
      </div>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.65rem',
          padding: '0.7rem 0.5rem',
          borderBottom: '1px solid #f3f4f6',
          cursor: 'pointer',
          minHeight: 48,
          fontSize: '0.95rem',
          marginBottom: '1rem',
        }}
      >
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          style={{ width: 22, height: 22, flexShrink: 0 }}
        />
        <span>
          {isLock
            ? 'הרכיב מחובר חשמלית למעגל ה-RSK ויש לו זינה'
            : 'אישרתי שכל החיבורים הפיזיים הושלמו'}
        </span>
      </label>
      <button
        type="button"
        disabled={!confirmed}
        onClick={onContinue}
        style={confirmed ? BTN_PRIMARY : BTN_DISABLED}
      >
        המשך
      </button>
    </div>
  );
}


// New step in the install wizard, between Preflight and Wiring.
// Collects the gate-level properties that don't change with the
// physical wiring: which product from the catalog, the opening
// height, and the two new gate-detail booleans. All fields are
// optional — the technician can skip-through if the office's
// pre-fill is already correct.
//
// Product picker uses the same double-confirm dialog pattern we
// shipped in PR 4 of the timeout work: stray taps shouldn't
// rewrite the office's catalog assignment without explicit
// acknowledgement.
function DetailsStep({
  gate,
  role,
  productId, onProductIdChange,
  openingHeight, onOpeningHeightChange,
  hasBuiltInAutoClose, onHasBuiltInAutoCloseChange,
  isContinuousWithLeveler, onIsContinuousWithLevelerChange,
  onContinue,
}) {
  // Phase 5 — residential control install: drop the leveler question,
  // it's a logistics-dock concept that has no analog in a private home.
  const showLeveler = role !== 'control';
  const [products, setProducts] = useState([]);
  const [productsError, setProductsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/technician/products')
      .then(res => { if (!cancelled) setProducts(res.data); })
      .catch(() => { if (!cancelled) setProductsError('שגיאה בטעינת הקטלוג'); });
    return () => { cancelled = true; };
  }, []);

  const currentSelection = productId || '';
  const currentProduct = products.find(p => String(p.id) === currentSelection);
  const originalProductId = gate && gate.product_id ? String(gate.product_id) : '';
  const isOverridingOffice =
    originalProductId !== '' && currentSelection !== originalProductId;

  // Apply the model choice directly — no confirmation dialog. The
  // technician reads the real model off the gate on site (customers only
  // drop map pins without models), so changing it is the routine expected
  // action; a confirm just slowed down installing many gates.
  const handleSelect = (id) => {
    onProductIdChange(id);
  };

  return (
    <div>
      <div style={{ fontSize: '0.88rem', color: '#374151', marginBottom: '0.85rem' }}>
        לפני שמתחילים בחיווט, נשמור את הנתונים על השער הפיזי. כל השדות
        אופציונליים — אפשר לדלג ולחזור לערוך אחר-כך.
      </div>

      {/* Product picker — same UX as the timeout work shipped:
          dropdown + confirmation dialog before any change actually
          takes. */}
      <DetailsFieldCard label="דגם השער (מהקטלוג)">
        <div style={{ fontSize: '0.82rem', color: '#475569', marginBottom: '0.45rem', lineHeight: 1.55 }}>
          {currentProduct
            ? `נבחר: ${currentProduct.name}${currentProduct.model_number ? ` (${currentProduct.model_number})` : ''}${currentProduct.speed_mps ? ` · ${currentProduct.speed_mps} מ״ש` : ''}`
            : 'לא נבחר דגם — מהירות הדגם לא תיכנס לחישוב זמן התנועה.'}
          {isOverridingOffice && (
            <span style={{ display: 'block', marginTop: '0.25rem', color: '#92400e' }}>
              ⚠ הדגם שונה ממה שהמשרד הגדיר במקור.
            </span>
          )}
        </div>
        {productsError ? (
          <div style={{ color: '#b91c1c', fontSize: '0.85rem' }}>{productsError}</div>
        ) : (
          <select
            value={currentSelection}
            onChange={(e) => handleSelect(e.target.value)}
            style={DETAILS_INPUT_STYLE}
          >
            <option value="">— ללא דגם —</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.model_number ? ` (${p.model_number})` : ''}
                {p.category_name ? ` · ${p.category_name}` : ''}
                {p.speed_mps ? ` · ${p.speed_mps} מ״ש` : ''}
              </option>
            ))}
          </select>
        )}
      </DetailsFieldCard>

      <DetailsFieldCard
        label="גובה הפתח (מטרים)"
        hint="מדידה פיזית של פתח השער. יחד עם מהירות הדגם בקטלוג, המערכת תחשב אוטומטית את הזמן שצריך לחכות בין פקודות במקום 45 שניות קבוע."
      >
        <input
          type="number"
          step="0.01"
          min="0"
          value={openingHeight || ''}
          onChange={(e) => onOpeningHeightChange && onOpeningHeightChange(e.target.value)}
          placeholder="לדוגמה: 2.5"
          style={DETAILS_INPUT_STYLE}
        />
      </DetailsFieldCard>

      <DetailsFieldCard
        label="יש סגירה אוטומטית בבקר עצמו?"
        hint="הבקר של השער סוגר את עצמו אוטומטית אחרי כמה זמן בלי שאנחנו צריכים לשלוח פקודה. בהמשך נשתמש בנתון הזה כדי לא לשלוח פקודות סגירה כפולות."
      >
        <YesNoToggle
          value={hasBuiltInAutoClose}
          onChange={onHasBuiltInAutoCloseChange}
        />
      </DetailsFieldCard>

      {showLeveler && (
        <DetailsFieldCard
          label="האם יש משווה גובה מתחת לדלת?"
          hint="משווה גובה הוא הרמפה שבין משאית לרצפת המחסן. רלוונטי לרציפי העמסה — לא לדלתות רגילות במפעל."
        >
          <YesNoToggle
            value={isContinuousWithLeveler}
            onChange={onIsContinuousWithLevelerChange}
          />
        </DetailsFieldCard>
      )}

      <button
        type="button"
        onClick={onContinue}
        style={BTN_PRIMARY}
      >
        {role === 'control' ? 'המשך לחיבור ה-Shelly' : 'המשך לבחירת חיווט'}
      </button>
    </div>
  );
}


// Small wrapper that gives every DetailsStep field the same card-
// like framing. Saves repetition + makes future additions feel
// consistent.
function DetailsFieldCard({ label, hint, children }) {
  return (
    <div style={{
      marginBottom: '1rem',
      padding: '0.75rem 0.9rem',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 6,
    }}>
      <label style={{
        display: 'block',
        fontSize: '0.88rem',
        fontWeight: 600,
        marginBottom: '0.4rem',
        color: '#1f2937',
      }}>
        {label}
      </label>
      {hint && (
        <div style={{ fontSize: '0.82rem', color: '#475569', marginBottom: '0.45rem', lineHeight: 1.55 }}>
          {hint}
        </div>
      )}
      {children}
    </div>
  );
}


// Yes/No toggle used by the gate-detail booleans. Two big buttons
// instead of a checkbox because (a) touch-friendly on the
// technician's phone, (b) makes the chosen state obvious at a
// glance, (c) matches the existing wizard's tap-the-card pattern.
function YesNoToggle({ value, onChange }) {
  const opts = [
    { id: false, label: 'לא' },
    { id: true,  label: 'כן' },
  ];
  return (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      {opts.map((opt) => {
        const selected = value === opt.id;
        return (
          <button
            key={String(opt.id)}
            type="button"
            onClick={() => onChange && onChange(opt.id)}
            style={{
              flex: 1,
              padding: '0.7rem 0.5rem',
              background: selected ? '#1a1a2e' : '#ffffff',
              color: selected ? '#ffffff' : '#1f2937',
              border: selected ? '2px solid #1a1a2e' : '1px solid #cbd5e1',
              borderRadius: 6,
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'Heebo, sans-serif',
              minHeight: 44,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}


const DETAILS_INPUT_STYLE = {
  width: '100%',
  padding: '0.65rem 0.75rem',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: '0.95rem',
  fontFamily: 'Heebo, sans-serif',
  boxSizing: 'border-box',
  minHeight: 44,
  background: '#ffffff',
};


function IdentifyStep({
  gate, wiringType, openingHeight, productId,
  hasBuiltInAutoClose, isContinuousWithLeveler,
  role,
  onProvisioned,
}) {
  const [mac, setMac] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmForce, setConfirmForce] = useState(false);

  const provision = async (force) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(
        `/api/technician/gates/${gate.id}/start-install`,
        {
          mac, force,
          // wiring_type is set automatically by the server when a
          // residential role is supplied; only forward it for the
          // industrial flow.
          wiring_type: role ? null : wiringType,
          opening_height_meters: openingHeight === '' ? null : openingHeight,
          product_id: productId === '' ? null : productId,
          has_built_in_auto_close: hasBuiltInAutoClose,
          is_continuous_with_leveler: isContinuousWithLeveler,
          role: role || null,
        },
      );
      onProvisioned(res.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        setConfirmForce(true);
      } else {
        setError(err.response?.data?.error || 'שגיאה בהתחלת התקנה');
      }
      setSubmitting(false);
    }
  };

  if (confirmForce) {
    return (
      <div>
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fde68a',
          borderRadius: 6,
          padding: '0.85rem',
          color: '#92400e',
          fontSize: '0.9rem',
          marginBottom: '1rem',
        }}>
          השער כבר משויך ל-Shelly. להתחיל מחדש (פרטי החיבור הקיימים יבוטלו)?
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={() => { setConfirmForce(false); }}
            disabled={submitting}
            style={BTN_SECONDARY}
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={() => provision(true)}
            disabled={submitting}
            style={submitting ? BTN_DISABLED : BTN_PRIMARY}
          >
            {submitting ? 'מבצע...' : 'התחל מחדש'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: '0.88rem', color: '#374151', marginBottom: '0.85rem' }}>
        הקלד את ה-MAC המודפס על ה-Shelly. אם אתה לא בטוח — אפשר גם להעתיק
        את שם המכשיר שמופיע על המדבקה.
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label style={{
          display: 'block', fontSize: '0.82rem',
          color: '#6b7280', marginBottom: '0.3rem',
        }}>
          MAC של ה-Shelly
        </label>
        <input
          type="text"
          value={mac}
          onChange={(e) => setMac(e.target.value)}
          placeholder="לדוגמה: AABBCC112233"
          style={{
            width: '100%',
            padding: '0.7rem 0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: '1rem',
            fontFamily: 'monospace',
            boxSizing: 'border-box',
            minHeight: 48,
          }}
          dir="ltr"
        />
      </div>
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 6, padding: '0.65rem 0.75rem',
          color: '#991b1b', fontSize: '0.85rem', marginBottom: '0.85rem',
        }}>
          {error}
        </div>
      )}
      <button
        type="button"
        onClick={() => provision(false)}
        disabled={submitting}
        style={submitting ? BTN_DISABLED : BTN_PRIMARY}
      >
        {submitting ? 'יוצר חיבור...' : 'המשך'}
      </button>
    </div>
  );
}

// Step 2 — instructions for connecting the Shelly to the site's WiFi and
// reading off its MAC. The technician spends this step *outside* Huri
// Connect (in the Shelly web UI), so the screen is pure read-only
// guidance. The MAC is read straight off the 192.168.33.1 AP page (the
// Wi-Fi status section) — no separate IP hunt needed.
function NetworkStep({
  gate, siteId, wiringType, openingHeight, productId,
  hasBuiltInAutoClose, isContinuousWithLeveler,
  role,
  onManual, onProvisioned,
}) {
  // Phase 5 — residential paired install uses the SAME BLE flow as
  // industrial. Field evidence shows the 2PM Gen4 BLE provisioning
  // works (handles profile switch to "switch", detached inputs,
  // MQTT, Wi-Fi all in one shot — critical because a manually-
  // configured 2PM left in "cover" profile responds to Switch.Set
  // RPCs with a no-op, which presents as "command received but the
  // gate didn't move"). The Shelly 1 Gen4 used for the lock has
  // only Switch:0 — our flow tries Switch:1 too but catches that
  // as a warning, so the rest of the provisioning still applies.
  //
  // The manual fallback stays one tap away via the "switch to
  // manual" link inside the BLE flow, in case the device has been
  // sitting unconfigured for more than 15 minutes and Secure
  // Provisioning has locked BLE-RPC.
  const [manualMode, setManualMode] = useState(false);

  // In the native app the automatic Bluetooth flow is the default; the
  // manual WiFi + MAC instructions below are the fallback, reachable via
  // the "switch to manual" link inside the flow. In a plain browser
  // there's no Bluetooth, so the manual instructions show directly.
  if (isNativeApp() && !manualMode) {
    return (
      <BleProvisionFlow
        gate={gate}
        siteId={siteId}
        wiringType={wiringType}
        openingHeight={openingHeight}
        productId={productId}
        hasBuiltInAutoClose={hasBuiltInAutoClose}
        isContinuousWithLeveler={isContinuousWithLeveler}
        role={role}
        onProvisioned={onProvisioned}
        onManual={() => setManualMode(true)}
      />
    );
  }

  return (
    <div>
      {role && (
        <div style={{
          background: '#EFF6FF', border: '1px solid #BFDBFE',
          borderRadius: 8, padding: '0.7rem 0.85rem',
          color: '#1E40AF', fontSize: '0.85rem',
          marginBottom: '0.85rem', lineHeight: 1.55,
        }}>
          הגעת לזרימה הידנית — אם הרכיב חדש ובחלון 15 הדקות, עדיף לחזור
          ולהשתמש ב-Bluetooth. אם הרכיב כבר ישב זמן רב — הזרימה הזו אמינה יותר.
        </div>
      )}

      <div style={{ fontSize: '0.88rem', color: '#374151', marginBottom: '0.85rem' }}>
        עכשיו נחבר את ה-Shelly לרשת ה-WiFi של האתר. עקוב אחרי השלבים — בסוף
        תחזור לכאן עם ה-<b>MAC</b> של הרכיב.
      </div>

      <ol style={{
        margin: 0,
        paddingInlineStart: '1.25rem',
        fontSize: '0.92rem',
        color: '#1f2937',
        lineHeight: 1.6,
      }}>
        <li style={{ marginBottom: '0.6rem' }}>
          בטלפון — צא להגדרות ה-Wi-Fi והתחבר לרשת בשם
          {' '}<b>{role === 'lock' ? 'Shelly1G4-...' : 'ShellyPlus...'}</b>
          {' '}(זו הרשת שהשלי משדר עכשיו).
        </li>
        <li style={{ marginBottom: '0.6rem' }}>
          פתח דפדפן והיכנס לכתובת:
          <div style={{ marginTop: '0.4rem' }}>
            <CopyRow label="כתובת ניהול השלי" value="192.168.33.1" />
          </div>
        </li>
        <li style={{ marginBottom: '0.6rem' }}>
          בממשק השלי: <b>Network → Wi-Fi → Enable Wi-Fi</b> — בחר את הרשת של
          האתר, הזן סיסמה ולחץ <b>Save settings</b>.
        </li>
        <li style={{ marginBottom: '0.6rem' }}>
          באותו דף <span dir="ltr">192.168.33.1</span>, בקטע <b>Wi-Fi status</b>,
          תראה את ה-<b>MAC address</b>. <b>העתק אותו</b> — תזין אותו במסך הבא.
        </li>
        <li style={{ marginBottom: '0.6rem' }}>
          עכשיו צא להגדרות ה-Wi-Fi בטלפון והחזר אותו ידנית לרשת של האתר
          (הטלפון לא יחליף רשת לבד).
        </li>
      </ol>

      <div style={{
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 6,
        padding: '0.7rem 0.85rem',
        color: '#1e40af',
        fontSize: '0.85rem',
        margin: '0.85rem 0 1rem',
      }}>
        💡 אל תחליף את רשת ה-Wi-Fi בטלפון לפני שהעתקת את ה-MAC —
        ברגע שתעזוב את רשת השלי, הדף <span dir="ltr">192.168.33.1</span> לא יהיה זמין.
      </div>

      <button
        type="button"
        onClick={onManual}
        style={BTN_PRIMARY}
      >
        יש לי את ה-MAC, המשך
      </button>
    </div>
  );
}

// Compose the one-paste Shelly setup script from credentials returned by
// start-install. The script runs inside the Shelly's JS sandbox and uses
// the Gen2 RPC API to:
//   1. Configure MQTT (TLS, broker, creds, prefix) in a single call
//   2. Set the device name
//   3. Disable the Shelly Cloud connection, so MQTT to our broker is
//      the only control channel (best-effort — like the detach calls,
//      a Gen4 firmware may report OK without actually applying it)
//   4. Reboot after a short delay so the changes take effect together
//
// Auth uses the broker's MASTER credentials, not per-device ones — HiveMQ
// Cloud Free Tier only accepts the single account configured at signup.
// Per-device isolation stays via the topic prefix (``mqtt_username``);
// the server identifies each device by that prefix on every inbound MQTT
// message. Pre-fix the Shelly's CONNECT was always coming back with
// status 5 (Not authorized) because the per-device password we minted
// was never registered with the broker.
//
// Profile (switch vs cover) is NOT handled here. A factory-reset 2PM
// Gen4 comes back in "cover" mode, which has no Switch.Set handler — so
// the gate must be in "switch" mode before commands work. Both RPC
// approaches we tried (Sys.SetConfig device.profile, Shelly.SetProfile)
// failed to take effect from inside the script sandbox in field
// testing, so the wizard's ScriptStep instructs the technician to flip
// the profile manually in the Shelly UI before pasting the script.
function buildSetupScript(creds, wiringType, role) {
  const username = creds.mqtt_username;       // topic prefix only
  const brokerUser = creds.broker_username;   // auth — master
  const brokerPass = creds.broker_password;   // auth — master
  const port = creds.broker_port || 8883;
  const server = `${creds.broker_host}:${port}`;
  // Phase 4.7 header — labels which wiring layout this script was
  // generated for. Helpful for support: a Shelly's script body
  // tells you what install profile it was provisioned with.
  const wiringLabel = role === 'lock'
    ? 'Residential lock (single-switch device, e.g. Shelly 1 Gen4)'
    : wiringType === 'toggle_with_stop'
      ? 'Toggle + Hermetic Lock (wiring 2)'
      : 'Open/Close separate (wiring 1)';
  // Phase 5 — residential lock device (Shelly 1 Gen4 typically). It
  // has ONE switch (Switch:0), no Switch:1. Calling Switch.SetConfig
  // on id:1 on a single-switch device returns an "Invalid argument"
  // error and the nested script callbacks abort. We branch the chain
  // here so single-switch devices get a flat
  // Switch:0 → MQTT → Sys → Cloud → Reboot path with no id:1 hop.
  const isLockDevice = role === 'lock';
  // Shelly Gen2/3/4 default: each input is "attached" to the
  // matching output, so a HIGH on Input N flips Switch N. For us
  // that means the closed-end limit switch on S1 would trigger our
  // O1 open-pulse relay every time the gate reaches the closed
  // position — the gate would re-open itself on every closure. Same
  // false-trigger story for S2 / O2 with fault transitions.
  //
  // The fix is the in_mode="detached" setting on each Switch:
  // input becomes a pure sensor reporting state, the relay only
  // moves when we tell it to over MQTT. Applies to both wiring
  // layouts — there's never a case in our product where the input
  // should drive the output directly.
  return `// Huri Connect — Shelly setup
// Auto-generated for: ${username}
// Wiring layout: ${wiringLabel}
// Paste once into: Settings → Scripts → Create script → Save → Start
// The Shelly will reboot at the end and join Huri-Connect automatically.

print("Huri Connect setup starting...");

${isLockDevice ? buildLockDeviceBody({ server, brokerUser, brokerPass, username }) : buildControlDeviceBody({ server, brokerUser, brokerPass, username })}
`;
}

function buildMqttCallBody({ server, brokerUser, brokerPass, username }) {
  // The MQTT → Sys → Cloud → Reboot tail is identical for every device.
  // We extract it so the per-role wrapper just chooses what comes
  // before (one or two Switch.SetConfig calls).
  return `
        Shelly.call(
          "MQTT.SetConfig",
          {
            config: {
              enable: true,
              server: "${server}",
              user: "${brokerUser}",
              pass: "${brokerPass}",
              topic_prefix: "${username}",
              // ssl_ca:"ca.pem" verifies the broker's TLS cert against
              // the Shelly's built-in CA bundle (includes Let's Encrypt).
              // "*" would accept any cert and leave the link open to a
              // man-in-the-middle on the customer's WiFi.
              ssl_ca: "ca.pem",
              enable_control: true,
              enable_rpc: true,
              rpc_ntf: true,
              status_ntf: true
            }
          },
          function (r, err, msg) {
            print("MQTT:", err === 0 ? "OK" : "ERR " + err + " " + msg);
            Shelly.call(
              "Sys.SetConfig",
              {
                config: {
                  device: {
                    name: "${username}"
                  }
                }
              },
              function (r2, err2, msg2) {
                print("Sys:", err2 === 0 ? "OK" : "ERR " + err2 + " " + msg2);
                Shelly.call(
                  "Cloud.SetConfig",
                  { config: { enable: false } },
                  function (r3, err3, msg3) {
                    print("Cloud off:", err3 === 0 ? "OK" : "ERR " + err3 + " " + msg3);
                    print("Rebooting in 3s...");
                    Shelly.call("Sys.Reboot", { delay_ms: 3000 });
                  }
                );
              }
            );
          }
        );`;
}

function buildLockDeviceBody(creds) {
  // Shelly 1 Gen4 (single relay, no Switch:1). Configure Switch:0 with
  // detached input (defensive — relay should only flip via MQTT), then
  // straight to MQTT.
  return `Shelly.call(
  "Switch.SetConfig",
  { id: 0, config: { in_mode: "detached" } },
  function (r0, err0, msg0) {
    print("Detach 0:", err0 === 0 ? "OK" : "ERR " + err0 + " " + msg0);
${buildMqttCallBody(creds)}
  }
);`;
}

function buildControlDeviceBody(creds) {
  // 2PM-style two-switch device. Detach BOTH inputs from their
  // matching outputs before MQTT.
  return `Shelly.call(
  "Switch.SetConfig",
  { id: 0, config: { in_mode: "detached" } },
  function (r0, err0, msg0) {
    print("Detach 0:", err0 === 0 ? "OK" : "ERR " + err0 + " " + msg0);
    Shelly.call(
      "Switch.SetConfig",
      { id: 1, config: { in_mode: "detached" } },
      function (r1, err1, msg1) {
        print("Detach 1:", err1 === 0 ? "OK" : "ERR " + err1 + " " + msg1);
${buildMqttCallBody(creds)}
      }
    );
  }
);`;
}

// Step 4 — two short tasks back in the Shelly UI: (1) flip the device
// profile to "switch" manually — required, a cover-mode device has no
// Switch.Set handler; (2) paste and run the generated setup script,
// which configures MQTT and the device name in one go.
function ScriptStep({ gate, creds, wiringType, role, onContinue }) {
  const script = buildSetupScript(creds, wiringType, role);
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(script);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Insecure-origin / older-browser fallback — user can still
      // select & copy the text from the visible code block below.
    }
  };

  return (
    <div>
      <div style={{ fontSize: '0.88rem', color: '#374151', marginBottom: '0.85rem' }}>
        עכשיו נחזור לממשק השלי כדי לסיים את ההגדרה — שלושה שלבים קצרים.
      </div>

      <div style={{ fontSize: '0.84rem', color: '#374151', marginBottom: '1rem' }}>
        פתח בדפדפן את ממשק השלי לפי כתובת ה-IP שלו ברשת.
      </div>

      {role !== 'lock' && (
        <div style={{
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: 6,
          padding: '0.75rem 0.9rem',
          fontSize: '0.88rem',
          color: '#1f2937',
          marginBottom: '1rem',
        }}>
          <div style={{ fontWeight: 700, marginBottom: '0.45rem', color: '#92400e' }}>
            שלב 1 — חובה: העבר את הרכיב למצב Switch
          </div>
          <div style={{ marginBottom: '0.4rem' }}>
            רכיב 2PM חדש מגיע לרוב במצב <b>Cover</b> — ובמצב הזה פקודות הפתיחה/סגירה
            לא יעבדו. בממשק השלי:
          </div>
          <ol style={{ margin: 0, paddingInlineStart: '1.2rem', lineHeight: 1.55 }}>
            <li><b>Settings → Device profile</b></li>
            <li>בחר <b>Switch</b></li>
            <li>לחץ <b>Save settings</b> — הרכיב יבצע ריסטארט</li>
          </ol>
        </div>
      )}

      <div style={{
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        padding: '0.75rem 0.9rem',
        fontSize: '0.88rem',
        color: '#1f2937',
        marginBottom: '1rem',
      }}>
        <div style={{ fontWeight: 700, marginBottom: '0.55rem' }}>
          {role === 'lock' ? 'הדבקת הסקריפט' : 'שלב 2 — הדבקת הסקריפט'}
        </div>

        <button
          type="button"
          onClick={onCopy}
          style={{
            ...BTN_PRIMARY,
            background: copied ? '#16a34a' : '#1a1a2e',
            marginBottom: '0.7rem',
          }}
        >
          {copied ? '✓ הסקריפט הועתק' : 'העתק את הסקריפט'}
        </button>

        <pre
          dir="ltr"
          style={{
            background: '#0f172a',
            color: '#e2e8f0',
            borderRadius: 6,
            padding: '0.7rem 0.85rem',
            fontSize: '0.72rem',
            lineHeight: 1.45,
            overflowX: 'auto',
            margin: '0 0 0.85rem',
            maxHeight: 200,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}
        >
          {script}
        </pre>

        <div style={{ fontWeight: 600, marginBottom: '0.45rem' }}>
          איפה מדביקים בשלי:
        </div>
        <ol style={{
          margin: 0,
          paddingInlineStart: '1.2rem',
          lineHeight: 1.55,
        }}>
          <li>פתח את התפריט (≡) בפינה השמאלית של ממשק השלי</li>
          <li>לחץ על <b>Scripts</b></li>
          <li>לחץ על <b>Create script</b></li>
          <li>מחק את הקוד הקיים והדבק את הסקריפט שהעתקת</li>
          <li>לחץ <b>Save</b>, ואז <b>Start</b></li>
        </ol>
        <div style={{ marginTop: '0.45rem', color: '#6b7280', fontSize: '0.82rem' }}>
          השלי יבצע ריסטארט אוטומטי תוך 3 שניות אחרי שהסקריפט ירוץ.
        </div>
      </div>

      {/*
        Step 3 — Detached input mode. The setup script tries to apply
        this automatically via Switch.SetConfig in_mode="detached",
        but in field testing on Shelly 2PM Gen4 firmware 1.7.5 the
        call returns success without actually applying — same script-
        sandbox quirk that bit us on the Cover→Switch profile change.
        Until the native + BLE flow (Phase 4.5) provisions over GATT
        instead of a script, the technician has to flip this manually.
        Don't drop this card even when the native app ships — keep it
        as the manual fallback for the install-script path, since
        any device that bypasses BLE will still hit this issue.

        The checklist below follows the exact flow the field expert
        confirmed works on Gen4 firmware 1.7.5. Critical because
        without it the closed-end limit switch on S1 will re-trigger
        the relay every time the gate closes, causing the gate to
        reopen itself in a loop.
      */}
      <div style={{
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 6,
        padding: '0.75rem 0.9rem',
        fontSize: '0.88rem',
        color: '#1f2937',
        marginBottom: '1rem',
      }}>
        <div style={{ fontWeight: 700, marginBottom: '0.45rem', color: '#92400e' }}>
          שלב 3 — חובה: הגדרת מצב מופרד (Detached) לשני המתגים
        </div>
        <div style={{ marginBottom: '0.45rem' }}>
          ברירת המחדל של השלי מקשרת בין הקלטים ליציאות. אצלנו <b>זה הרסני</b> —
          מתג סוף-דרך על Input 0 יפעיל את Output 0 בעצמו והשער ייפתח שוב כל
          פעם שהוא נסגר. הסקריפט מנסה לעשות את ההפרדה אוטומטית, אבל בקושחה
          הנוכחית של Gen4 זה לא תמיד תופס. בצע ידנית:
        </div>
        <ol style={{ margin: 0, paddingInlineStart: '1.2rem', lineHeight: 1.6 }}>
          <li>במסך <b>Home</b> של השלי — לחץ על הקובייה של <b>Output (0)</b></li>
          <li>לחץ על <b>Settings</b> בתפריט העליון</li>
          <li>בחר <b>Input/Output Settings</b></li>
          <li>תחת <b>Select input mode for Input (0)</b> — בחר <b>Detached</b></li>
          <li>לחץ <b>Save settings</b></li>
          <li>חזור ל-<b>Home</b>, לחץ על <b>Output (1)</b>, וחזור על הפעולה (Settings → Input/Output Settings → Detached → Save)</li>
        </ol>
        <div style={{
          marginTop: '0.55rem',
          padding: '0.5rem 0.7rem',
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: 5,
          fontSize: '0.83rem',
          color: '#9a3412',
        }}>
          <b>אימות:</b> חזור למסך Home. אייקון השרשרת (חוליות) בצד ימין בין
          ה-Input ל-Output צריך <b>להיעלם</b> — לשני הערוצים. אם הוא עוד שם,
          ההפרדה לא נשמרה ויש לחזור על השלב.
        </div>
      </div>

      <button
        type="button"
        onClick={onContinue}
        style={BTN_PRIMARY}
      >
        סיימתי — Switch + סקריפט + הפרדה, המשך
      </button>
    </div>
  );
}

function WaitingStep({ gate, role, onAlive }) {
  const [elapsed, setElapsed] = useState(0);
  const [brokerOk, setBrokerOk] = useState(null);
  const [stalled, setStalled] = useState(false);
  const [pollKey, setPollKey] = useState(0);  // bump to restart polling

  useEffect(() => {
    let cancelled = false;
    const start = Date.now();

    const tick = async () => {
      try {
        // Phase 5 — residential paired install: tell the server which
        // device's status we're polling (control vs lock). Without it
        // the backend's filter_by(gate_id=...).first() can return the
        // OTHER device and the wizard stalls on the wrong one.
        const url = role
          ? `/api/technician/gates/${gate.id}/install-status?role=${encodeURIComponent(role)}`
          : `/api/technician/gates/${gate.id}/install-status`;
        const res = await axios.get(url);
        if (cancelled) return;
        setBrokerOk(res.data.device_connected);
        // Advance as soon as the device has checked in at least once —
        // not only while it's "recently" alive. On a weak site WiFi the
        // Shelly may connect just long enough for us to hear it (and
        // auto-complete the install) and then drop; the open/close test
        // that follows is the real proof of a working link.
        if (res.data.is_recently_alive || res.data.has_connected) {
          onAlive();
          return;
        }
      } catch (e) {
        // Ignore transient errors during the polling loop — the next
        // tick will retry. Network blips are common in install sites.
      }
      const e = Math.floor((Date.now() - start) / 1000);
      if (cancelled) return;
      setElapsed(e);
      if (e >= 300) {
        setStalled(true);
        return;  // stop polling, prompt the user
      }
    };

    tick();  // immediate first call
    const interval = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [gate.id, onAlive, pollKey, role]);

  if (stalled) {
    return (
      <div>
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 6, padding: '0.85rem',
          color: '#991b1b', fontSize: '0.9rem', marginBottom: '1rem',
        }}>
          לא קלטנו heartbeat מה-Shelly במשך 5 דקות.
          <ol style={{ margin: '0.5rem 0 0', paddingInlineStart: '1.2rem' }}>
            <li>ה-Shelly מחובר לחשמל?</li>
            <li>ה-WiFi של הלקוח עובד?</li>
            <li>הקלדת את שם המכשיר נכון בממשק ה-Shelly?</li>
          </ol>
        </div>
        <button
          type="button"
          onClick={() => { setStalled(false); setElapsed(0); setPollKey((k) => k + 1); }}
          style={BTN_PRIMARY}
        >
          נסה שוב
        </button>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: '1rem 0' }}>
      <div style={{
        width: 56, height: 56,
        margin: '0 auto 0.85rem',
        border: '4px solid #e5e7eb',
        borderTop: '4px solid #1a1a2e',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: '0.95rem', color: '#374151', marginBottom: '0.4rem' }}>
        ממתינים שה-Shelly יתחבר לשרת...
      </div>
      <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>
        זמן שעבר: {elapsed} שניות
      </div>
      <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '0.5rem' }}>
        מצב הרכיב: {brokerOk === null ? '...' : brokerOk ? 'מחובר' : 'ממתין'}
      </div>
      {/* Safety net: if the auto-advance hasn't fired after a short wait,
          let the technician proceed to the open/close test manually —
          the test itself confirms whether the device responds. */}
      {elapsed >= 25 && (
        <button
          type="button"
          onClick={onAlive}
          style={{ ...BTN_SECONDARY, marginTop: '1rem' }}
        >
          עבור לבדיקות בכל זאת
        </button>
      )}
    </div>
  );
}

function TestButton({ label, color, onClick, state, error }) {
  const isWorking = state === 'sending';
  const isAck = state === 'ack';
  const isFail = state === 'failed';
  return (
    <div style={{ marginBottom: '1rem' }}>
      <button
        type="button"
        onClick={onClick}
        disabled={isWorking}
        style={{
          display: 'block',
          width: '100%',
          padding: '1rem',
          background: color,
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          fontSize: '1.1rem',
          fontWeight: 700,
          cursor: isWorking ? 'wait' : 'pointer',
          opacity: isWorking ? 0.7 : 1,
          fontFamily: 'Heebo, sans-serif',
          minHeight: 64,
        }}
      >
        {isWorking ? 'שולח...' : label}
      </button>
      {isAck && (
        <div style={{
          marginTop: '0.4rem', fontSize: '0.85rem',
          color: '#16a34a',
        }}>
          ✓ הפקודה התקבלה ב-Shelly
        </div>
      )}
      {isFail && (
        <div style={{
          marginTop: '0.4rem', fontSize: '0.85rem',
          color: '#b91c1c',
        }}>
          ✗ {error || 'הפקודה נכשלה'} — נסה שוב.
        </div>
      )}
    </div>
  );
}

// Translate known raw device/RPC errors into plain Hebrew. A cover-mode
// Shelly has no Switch.Set handler, so the RPC comes back as
// "No handler for Switch.Set" — useless to a technician. Anything we
// don't recognise falls through unchanged.
function friendlyTestError(raw) {
  const s = String(raw || '');
  if (s.includes('Switch.Set') || s.includes('No handler')) {
    return 'הרכיב במצב Cover — יש להעבירו ל-Switch mode ולנסות שוב.';
  }
  if (s === 'timeout') {
    return 'הרכיב לא הגיב בזמן — בדוק שהוא מחובר ונסה שוב.';
  }
  return raw || 'הפקודה נכשלה';
}

// Shared command-run + polling for every test cell in TestingStep.
// Returns a function the caller invokes with the command type; the
// passed-in setter manages the cell's local state (idle/sending/ack/
// failed) so each cell renders independently. Lives outside the
// component so both the legacy and toggle test layouts can share it
// without re-binding state on every render.
function makeRunTest(gateId) {
  return async function runTest(commandType, setter) {
    setter({ state: 'sending', error: null, confirmed: false });
    try {
      const res = await axios.post(
        `/api/technician/gates/${gateId}/test-command`,
        { command_type: commandType },
      );
      const cmdId = res.data.command_id;
      // Poll command status until it terminates. Cap at ~30 s so a
      // hung command doesn't keep the spinner forever.
      const start = Date.now();
      const poll = async () => {
        try {
          const r = await axios.get(
            `/api/technician/gates/${gateId}/test-command/${cmdId}`,
          );
          const status = r.data.status;
          if (status === 'acknowledged') {
            setter({ state: 'ack', error: null, confirmed: false });
            return;
          }
          if (status === 'failed' || status === 'timeout') {
            setter({
              state: 'failed',
              error: friendlyTestError(r.data.error || status),
              confirmed: false,
            });
            return;
          }
          if (Date.now() - start > 30000) {
            setter({
              state: 'failed',
              error: friendlyTestError('timeout'),
              confirmed: false,
            });
            return;
          }
          setTimeout(poll, 1000);
        } catch (err) {
          setter({ state: 'failed', error: 'שגיאת רשת', confirmed: false });
        }
      };
      setTimeout(poll, 1000);
    } catch (err) {
      setter({
        state: 'failed',
        error: err.response?.data?.error || 'שגיאה',
        confirmed: false,
      });
    }
  };
}


function renderConfirm(question, val, setter) {
  if (val.state !== 'ack' || val.confirmed) return null;
  return (
    <div style={{
      marginTop: '0.5rem',
      background: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: 6,
      padding: '0.65rem 0.75rem',
    }}>
      <div style={{ fontSize: '0.88rem', marginBottom: '0.5rem' }}>
        ❓ {question}
      </div>
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <button
          type="button"
          onClick={() => setter({ ...val, confirmed: true })}
          style={{ ...BTN_PRIMARY, background: '#16a34a' }}
        >
          ✓ כן
        </button>
        <button
          type="button"
          onClick={() => setter({ state: 'idle', error: null, confirmed: false })}
          style={BTN_SECONDARY}
        >
          ✗ לא, נסה שוב
        </button>
      </div>
    </div>
  );
}


function TestingStep({ gate, wiringType, role, onContinue }) {
  // Dispatch to the right test layout. Each layout's verification
  // story is meaningfully different — toggle wiring needs lock and
  // unlock proofs alongside the movement tests, and the lock test
  // requires the technician to physically attempt opening from the
  // controller (which the legacy layout has no equivalent for).
  //
  // Phase 5 — residential paired install:
  //   * role === 'lock': we just provisioned the Plus 1 on the RSK
  //     circuit. The auto-unlock that ran at the end of start-install
  //     already closed the relay; testing is implicit. Skip straight
  //     to the success screen with the "next: control" CTA.
  //   * role === 'control': the lock device is separate, so we only
  //     verify open/close — exactly the legacy testing flow.
  if (role === 'lock') {
    return <TestingStepResidentialLock gate={gate} onContinue={onContinue} />;
  }
  if (role === 'control') {
    return <TestingStepLegacy gate={gate} onContinue={onContinue} />;
  }
  if (wiringType === 'toggle_with_stop') {
    return <TestingStepToggle gate={gate} onContinue={onContinue} />;
  }
  return <TestingStepLegacy gate={gate} onContinue={onContinue} />;
}


function TestingStepLegacy({ gate, onContinue }) {
  // Two parallel sub-flows — each test direction has its own send state
  // and its own physical-confirmation flag. Both must turn green before
  // the technician can finish.
  const [open, setOpen] = useState({ state: 'idle', error: null, confirmed: false });
  const [close, setClose] = useState({ state: 'idle', error: null, confirmed: false });
  const runTest = makeRunTest(gate.id);

  const bothConfirmed = open.confirmed && close.confirmed;

  return (
    <div>
      <div style={{ fontSize: '0.88rem', color: '#374151', marginBottom: '0.85rem' }}>
        לחץ כל אחד מהכפתורים ובדוק שהשער אכן מגיב.
      </div>

      <TestButton
        label="▲ פתח"
        color="#16a34a"
        state={open.state}
        error={open.error}
        onClick={() => runTest('open', setOpen)}
      />
      {renderConfirm('האם השער נפתח?', open, setOpen)}
      {open.confirmed && (
        <div style={{ fontSize: '0.85rem', color: '#16a34a', marginBottom: '1rem' }}>
          ✓ פתיחה אומתה
        </div>
      )}

      <TestButton
        label="▼ סגור"
        color="#dc2626"
        state={close.state}
        error={close.error}
        onClick={() => runTest('close', setClose)}
      />
      {renderConfirm('האם השער נסגר?', close, setClose)}
      {close.confirmed && (
        <div style={{ fontSize: '0.85rem', color: '#16a34a', marginBottom: '1rem' }}>
          ✓ סגירה אומתה
        </div>
      )}

      <button
        type="button"
        onClick={onContinue}
        disabled={!bothConfirmed}
        style={bothConfirmed ? BTN_PRIMARY : BTN_DISABLED}
      >
        המשך לסיום
      </button>
    </div>
  );
}


// Phase 4.7 — testing layout for toggle_with_stop gates. Four
// sequential cells that prove the wiring works end-to-end. Order
// matters and is field-validated: lock → unlock → open → close.
//
//   1. נעל        — sticky O2 off (NO opens, STOP bridge breaks);
//                   technician verifies a physical open attempt
//                   from the controller's own remote / button
//                   does NOT move the gate. This is the critical
//                   wiring proof — if the gate moves, the STOP
//                   circuit isn't actually broken and we'd give
//                   the customer a false sense of security.
//   2. שחרר נעילה — sticky O2 on (NO closes, bridge restored);
//                   physical open attempt should work again. This
//                   also puts the O2 relay into a known operable
//                   state for the rest of the install — without
//                   it the relay sometimes lingers in its default
//                   off state and the open/close tests below fail
//                   because the motor's STOP signal is still active.
//   3. פתח        — STEP pulse should physically open the gate.
//   4. סגור       — STEP pulse should physically close it.
//
// All four must be confirmed before "המשך לסיום" enables.
function TestingStepToggle({ gate, onContinue }) {
  const [open, setOpen] = useState({ state: 'idle', error: null, confirmed: false });
  const [close, setClose] = useState({ state: 'idle', error: null, confirmed: false });
  const [lock, setLock] = useState({ state: 'idle', error: null, confirmed: false });
  const [unlock, setUnlock] = useState({ state: 'idle', error: null, confirmed: false });
  const runTest = makeRunTest(gate.id);

  const allConfirmed = open.confirmed && close.confirmed && lock.confirmed && unlock.confirmed;

  return (
    <div>
      <div style={{ fontSize: '0.88rem', color: '#374151', marginBottom: '0.85rem' }}>
        לחיווט חיווט 2 יש ארבעה שלבי בדיקה. בצע אותם לפי הסדר. ניתן לעצור באמצע
        ולחזור — סטטוס כל בדיקה נשמר עד שתסיים אותה.
      </div>

      <TestSectionHeader
        index={1}
        label="נעילה הרמטית — חיוני!"
        done={lock.confirmed}
        critical
      />
      <div style={{
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 6,
        padding: '0.65rem 0.75rem',
        fontSize: '0.85rem',
        color: '#92400e',
        marginBottom: '0.6rem',
      }}>
        אחרי לחיצה על "הפעל נעילה", נסה לפתוח את השער <b>מהשלט הפיזי או מכפתור
        על הבקר</b>. השער <b>לא צריך לזוז</b>. אם זז — החיווט של O2 ל-STOP
        circuit אינו תקין; עצור והחזר את הבקר.
      </div>
      <TestButton
        label="🔒 הפעל נעילה"
        color="#1e3a8a"
        state={lock.state}
        error={lock.error}
        onClick={() => runTest('lock', setLock)}
      />
      {renderConfirm('האם השער לא זז כשניסית לפתוח מהשלט?', lock, setLock)}

      <TestSectionHeader index={2} label="שחרור נעילה" done={unlock.confirmed} />
      <div style={{
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: 6,
        padding: '0.65rem 0.75rem',
        fontSize: '0.85rem',
        color: '#166534',
        marginBottom: '0.6rem',
      }}>
        אחרי לחיצה על "שחרר נעילה", נסה שוב לפתוח מהשלט הפיזי. השער
        <b> אמור לזוז</b>. שלב זה גם מכניס את הריליי של ה-STOP למצב פעיל
        ידוע — חיוני להצלחת בדיקות הפתיחה/סגירה הבאות.
      </div>
      <TestButton
        label="🔓 שחרר נעילה"
        color="#15803d"
        state={unlock.state}
        error={unlock.error}
        onClick={() => runTest('unlock', setUnlock)}
      />
      {renderConfirm('האם השער זז שוב כשניסית לפתוח מהשלט?', unlock, setUnlock)}

      <TestSectionHeader index={3} label="פתיחה" done={open.confirmed} />
      <TestButton
        label="▲ פתח"
        color="#16a34a"
        state={open.state}
        error={open.error}
        onClick={() => runTest('open', setOpen)}
      />
      {renderConfirm('האם השער נפתח?', open, setOpen)}

      <TestSectionHeader index={4} label="סגירה" done={close.confirmed} />
      <TestButton
        label="▼ סגור"
        color="#dc2626"
        state={close.state}
        error={close.error}
        onClick={() => runTest('close', setClose)}
      />
      {renderConfirm('האם השער נסגר?', close, setClose)}

      <button
        type="button"
        onClick={onContinue}
        disabled={!allConfirmed}
        style={{ ...(allConfirmed ? BTN_PRIMARY : BTN_DISABLED), marginTop: '1rem' }}
      >
        המשך לסיום
      </button>
    </div>
  );
}


function TestSectionHeader({ index, label, done, critical }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginTop: '0.8rem',
      marginBottom: '0.4rem',
    }}>
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22, height: 22,
        borderRadius: '50%',
        background: done ? '#16a34a' : (critical ? '#fde68a' : '#e5e7eb'),
        color: done ? '#ffffff' : (critical ? '#92400e' : '#4b5563'),
        fontSize: '0.78rem',
        fontWeight: 700,
        flexShrink: 0,
      }}>
        {done ? '✓' : index}
      </span>
      <span style={{
        fontSize: '0.92rem',
        fontWeight: 600,
        color: critical && !done ? '#92400e' : '#1f2937',
      }}>
        {label}
      </span>
    </div>
  );
}

// Phase 4.5 — runs only after the open/close/lock tests pass (BLE flow).
// Locks the Shelly's web/RPC UI with its per-device password so nobody
// on the customer's WiFi can reconfigure it. Best-effort: if it fails
// the gate is already working, so the technician can continue (and Huri
// support can set the password later from the device page).
function SecuringStep({ bleCtx, password, onDone }) {
  const [stage, setStage] = useState('connecting');
  const [failed, setFailed] = useState(false);

  const run = useCallback(() => {
    let cancelled = false;
    setFailed(false);
    secureDevice(bleCtx.deviceId, {
      realm: bleCtx.realm,
      password,
      onStage: (s) => { if (!cancelled) setStage(s); },
    })
      .then(() => { if (!cancelled) onDone(); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [bleCtx, password, onDone]);

  useEffect(run, [run]);

  if (failed) {
    return (
      <div style={{ textAlign: 'center', padding: '0.5rem' }}>
        <div style={{ fontSize: '0.95rem', color: '#92400e', marginBottom: '0.5rem' }}>
          לא הצלחנו לקבוע סיסמת אבטחה לרכיב.
        </div>
        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem', lineHeight: 1.5 }}>
          השער עובד תקין. אפשר לנסות שוב, או להמשיך — ניתן לקבוע את הסיסמה
          מאוחר יותר.
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" onClick={run} style={BTN_SECONDARY}>נסה שוב</button>
          <button type="button" onClick={onDone} style={BTN_PRIMARY}>המשך</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: '1.5rem 0.5rem' }}>
      <div style={{
        width: 56, height: 56, margin: '0 auto 0.85rem',
        border: '4px solid #e5e7eb', borderTop: '4px solid #1a1a2e',
        borderRadius: '50%', animation: 'spin 1s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: '0.95rem', color: '#1e40af' }}>
        {stage === 'config_auth' ? 'קובע סיסמת אבטחה לרכיב…' : 'מתחבר לרכיב לאבטחה…'}
      </div>
      <div style={{ fontSize: '0.82rem', color: '#9ca3af', marginTop: '0.5rem' }}>
        אל תתרחק מהרכיב.
      </div>
    </div>
  );
}

function SuccessStep({ gate, role, onClose }) {
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Phase 5 — for a paired-install gate, complete-install marks the
    // gate fully installed only when BOTH devices are present. Until
    // then the lock-only success screen sends the technician to the
    // control install instead of "next gate".
    axios.post(`/api/technician/gates/${gate.id}/complete-install`)
      .then(() => { if (!cancelled) setDone(true); })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'שגיאה בסיום');
      });
    return () => { cancelled = true; };
  }, [gate.id]);

  if (error) {
    return (
      <div>
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 6, padding: '0.85rem',
          color: '#991b1b', fontSize: '0.9rem', marginBottom: '1rem',
        }}>
          {error}
        </div>
        <button type="button" onClick={onClose} style={BTN_SECONDARY}>
          סגור
        </button>
      </div>
    );
  }

  if (!done) {
    return (
      <div style={{ textAlign: 'center', padding: '1.5rem 0', color: '#6b7280' }}>
        מסיים...
      </div>
    );
  }

  if (role === 'lock') {
    return (
      <div style={{ textAlign: 'center', padding: '0.5rem 0 1rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.6rem' }}>🔓</div>
        <div style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          רכיב הנעילה הותקן
        </div>
        <div style={{
          fontSize: '0.9rem', color: '#374151', marginBottom: '1.2rem',
          lineHeight: 1.55,
        }}>
          המעגל סגור והשער יכול לזוז.<br />
          המשך עכשיו להתקנת <b>רכיב הפתיחה (2PM)</b>.
        </div>
        <button type="button" onClick={onClose} style={BTN_PRIMARY}>
          חזור והתקן את רכיב הפתיחה ›
        </button>
      </div>
    );
  }

  if (role === 'control') {
    return (
      <div style={{ textAlign: 'center', padding: '0.5rem 0 1rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.6rem' }}>🎉</div>
        <div style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          השער "{gate.name}" מוכן לפעולה
        </div>
        <div style={{
          fontSize: '0.9rem', color: '#374151', marginBottom: '1.2rem',
          lineHeight: 1.55,
        }}>
          שני הרכיבים מותקנים. הלקוח כבר יכול לפתוח ולנעול את השער
          מהאפליקציה.
        </div>
        <button type="button" onClick={onClose} style={BTN_PRIMARY}>
          סיים והמשך לשער הבא ›
        </button>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: '0.5rem 0 1rem' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '0.6rem' }}>🎉</div>
      <div style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '1.2rem' }}>
        השער "{gate.name}" הותקן בהצלחה
      </div>
      <button type="button" onClick={onClose} style={BTN_PRIMARY}>
        סיים והמשך לשער הבא ›
      </button>
    </div>
  );
}

function TestingStepResidentialLock({ gate, onContinue }) {
  // Phase 5 — verify the lock device physically before we let the
  // technician move on to the control install.
  //
  // Two-step proof:
  //   1. שחרר נעילה — sends "unlock" → relay ON → RSK bridge closes
  //      → physical test: shutter remote/button → gate SHOULD move.
  //   2. נעל את השער — sends "lock" → relay OFF → bridge breaks →
  //      physical test: shutter remote/button → gate should NOT move.
  // Both confirmations are required before "המשך" enables. Without
  // this, an install with miswired NO/NC contacts (or a stuck relay)
  // would slip through and the customer's "open" tap would do nothing.
  const [unlockState, setUnlockState] = useState({ state: 'idle', error: null, confirmed: false });
  const [lockState, setLockState] = useState({ state: 'idle', error: null, confirmed: false });
  const runTest = makeRunTest(gate.id);

  const bothConfirmed = unlockState.confirmed && lockState.confirmed;

  return (
    <div>
      <div style={{ fontSize: '0.88rem', color: '#374151', marginBottom: '0.85rem' }}>
        ננסה לשחרר ולנעול את השער דרך הרכיב, ותאשר ידנית שזה אכן עבד.
        בלי האישור הזה אי אפשר להתקדם לרכיב הפתיחה.
      </div>

      <TestSectionHeader
        index={1}
        label="שחרור נעילה"
        done={unlockState.confirmed}
      />
      <TestButton
        label="🔓 שחרר נעילה"
        color="#16a34a"
        state={unlockState.state}
        error={unlockState.error}
        onClick={() => runTest('unlock', setUnlockState)}
      />
      {renderConfirm(
        'נסה לפתוח את השער מהשלט/הבקר. השער זז?',
        unlockState,
        setUnlockState,
      )}
      {unlockState.confirmed && (
        <div style={{ fontSize: '0.85rem', color: '#16a34a', marginBottom: '1rem' }}>
          ✓ שחרור אומת — המעגל סגור והשער ניתן לתפעול.
        </div>
      )}

      <TestSectionHeader
        index={2}
        label="נעילה הרמטית"
        done={lockState.confirmed}
      />
      <TestButton
        label="🔒 נעל את השער"
        color="#dc2626"
        state={lockState.state}
        error={lockState.error}
        onClick={() => runTest('lock', setLockState)}
      />
      {renderConfirm(
        'נסה לפתוח את השער מהשלט/הבקר. השער לא זז? (זאת אומרת שהנעילה עובדת)',
        lockState,
        setLockState,
      )}
      {lockState.confirmed && (
        <div style={{ fontSize: '0.85rem', color: '#16a34a', marginBottom: '1rem' }}>
          ✓ נעילה אומתה — מעגל ה-RSK נפתח והמנוע אינו עובד.
        </div>
      )}

      <button
        type="button"
        onClick={async () => {
          // Before moving on to control install, leave the relay in the
          // UNLOCKED state so the gate can be tested. The customer's UI
          // also starts unlocked (residential_lock_engaged was cleared
          // server-side after the lock install). Running this is best-
          // effort — even if it fails, the technician already saw
          // both tests pass.
          try { await runTest('unlock', setUnlockState); } catch { /* ignore */ }
          onContinue();
        }}
        disabled={!bothConfirmed}
        style={bothConfirmed ? BTN_PRIMARY : BTN_DISABLED}
      >
        שני המבחנים עברו — המשך
      </button>
    </div>
  );
}

function CompletedResumeView({ gate, onReinstall, onClose }) {
  return (
    <div>
      <div style={{
        background: '#dcfce7', border: '1px solid #86efac',
        borderRadius: 6, padding: '0.85rem',
        color: '#166534', fontSize: '0.95rem', marginBottom: '1rem',
      }}>
        ✓ השער "{gate.name}" כבר הותקן בהצלחה.
      </div>
      <button
        type="button"
        onClick={onClose}
        style={{ ...BTN_PRIMARY, marginBottom: '0.5rem' }}
      >
        סגור
      </button>
      <button
        type="button"
        onClick={onReinstall}
        style={BTN_SECONDARY}
      >
        התחל מחדש
      </button>
    </div>
  );
}

function FailedResumeView({ gate, onReinstall, onClose }) {
  return (
    <div>
      <div style={{
        background: '#fef2f2', border: '1px solid #fecaca',
        borderRadius: 6, padding: '0.85rem',
        color: '#991b1b', fontSize: '0.92rem', marginBottom: '1rem',
      }}>
        <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>
          ההתקנה סומנה ככשלון
        </div>
        {gate.installation_notes ? (
          <div style={{ fontSize: '0.85rem' }}>
            הערה: {gate.installation_notes}
          </div>
        ) : (
          <div style={{ fontSize: '0.85rem', color: '#7f1d1d' }}>
            (אין הערה שמורה)
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onReinstall}
        style={{ ...BTN_PRIMARY, marginBottom: '0.5rem' }}
      >
        נסה התקנה שוב
      </button>
      <button type="button" onClick={onClose} style={BTN_SECONDARY}>
        סגור
      </button>
    </div>
  );
}

export default TechnicianApp;
